import { Button } from '@heroui/react';
import { cn } from '@hs/ui';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useCallback, useEffect, useState, type FC } from 'react';

import {
  adjustDurationField,
  clampDurationField,
  formatTargetTime,
  parseDurationFieldInput,
} from './timeUtils';
import type { TimerDurationField, TimerTimeSettingProps } from './types';

const FIELDS: TimerDurationField[] = ['hours', 'minutes', 'seconds'];
const ICON_STROKE = 1.25;

type FieldEditorProps = {
  field: TimerDurationField;
  value: number;
  editable: boolean;
  onAdjust: (field: TimerDurationField, direction: 1 | -1) => void;
  onCommit: (field: TimerDurationField, value: number) => void;
};

const FieldEditor: FC<FieldEditorProps> = ({
  field,
  value,
  editable,
  onAdjust,
  onCommit,
}) => {
  const [draft, setDraft] = useState(() => String(value).padStart(2, '0'));

  useEffect(() => {
    setDraft(String(value).padStart(2, '0'));
  }, [value]);

  const commitDraft = useCallback(() => {
    const parsed = parseDurationFieldInput(field, draft);
    if (parsed == null) {
      setDraft(String(value).padStart(2, '0'));
      return;
    }
    onCommit(field, parsed);
    setDraft(String(parsed).padStart(2, '0'));
  }, [draft, field, onCommit, value]);

  if (!editable) {
    return (
      <span className="min-w-[2ch] text-center tabular-nums">{String(value).padStart(2, '0')}</span>
    );
  }

  return (
    <div className="flex flex-col items-center">
      <Button
        variant="ghost"
        size="sm"
        isIconOnly
        aria-label={`增加${field}`}
        className="size-5 min-w-5 text-muted-foreground"
        onPress={() => onAdjust(field, 1)}
      >
        <IconChevronUp className="size-3" stroke={ICON_STROKE} />
      </Button>
      <input
        aria-label={`${field} input`}
        className={cn(
          'w-[2.4ch] border-none bg-transparent p-0 text-center text-sm tabular-nums',
          'text-muted-foreground outline-none focus:text-foreground',
        )}
        inputMode="numeric"
        maxLength={2}
        value={draft}
        onChange={(event) => setDraft(event.target.value.replace(/\D/g, '').slice(0, 2))}
        onBlur={commitDraft}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.currentTarget.blur();
          }
        }}
      />
      <Button
        variant="ghost"
        size="sm"
        isIconOnly
        aria-label={`减少${field}`}
        className="size-5 min-w-5 text-muted-foreground"
        onPress={() => onAdjust(field, -1)}
      >
        <IconChevronDown className="size-3" stroke={ICON_STROKE} />
      </Button>
    </div>
  );
};

export const TimerTimeSetting: FC<TimerTimeSettingProps> = ({ value, onChange, editable }) => {
  const handleAdjust = useCallback(
    (field: TimerDurationField, direction: 1 | -1) => {
      onChange(adjustDurationField(value, field, direction));
    },
    [onChange, value],
  );

  const handleCommit = useCallback(
    (field: TimerDurationField, nextValue: number) => {
      onChange({ ...value, [field]: clampDurationField(field, nextValue) });
    },
    [onChange, value],
  );

  if (!editable) {
    return (
      <span className="text-sm tabular-nums text-muted-foreground">{formatTargetTime(value)}</span>
    );
  }

  return (
    <div className="flex items-center gap-0.5 text-sm text-muted-foreground" role="group" aria-label="设置目标时长">
      {FIELDS.map((field, index) => (
        <div key={field} className="flex items-center">
          {index > 0 ? <span className="px-0.5">:</span> : null}
          <FieldEditor
            field={field}
            value={value[field]}
            editable={editable}
            onAdjust={handleAdjust}
            onCommit={handleCommit}
          />
        </div>
      ))}
    </div>
  );
};
